export function AboutPage() {
  return (
    <div className="page prose">
      <h1>About joltstream</h1>

      <h2>Mission</h2>
      <p>
        joltstream exists to give drivers a way to collectively gather data
        about the state of the roads they use, anonymously and without anyone having
        to remember to report anything. The more drivers who contribute
        passively over time, the harder the resulting dataset is for
        councils and highways authorities to ignore. The data is open;
        the goal is accountability, not surveillance.
      </p>
      <p>
        We are in open beta. If you'd like to be part of it — install the
        app, stream your drives, and let us know what works and what
        doesn't — email{' '}
        <a href="mailto:beta@joltstream.app?subject=joltstream%20beta">
          beta@joltstream.app
        </a>
        . No signup is required to contribute; we just like hearing from
        people who do.
      </p>

      <h2>What it does</h2>
      <p>
        joltstream collects two kinds of data as you drive:
      </p>
      <ul>
        <li>
          <strong>Hits</strong> — vertical-acceleration spikes when you go over a road defect
        </li>
        <li>
          <strong>Swerves</strong> — lateral acceleration plus a sharp heading change when you avoid one
        </li>
      </ul>
      <p>
        Neither signal is trusted on its own. Hits need to clear a speed gate
        and a sigma threshold. Swerves only count when several different drivers
        all swerve at the same point.
      </p>

      <h2>What it doesn't do</h2>
      <ul>
        <li>No login. No account. No advertising ID.</li>
        <li>No continuous trip track. Only the individual jolts ever leave your device — not the path between them.</li>
        <li>No date or time of day. Jolts are stored without any absolute timestamp, so they can't be tied to when you were on a road.</li>
        <li>About 75 m around the start and end of every trip is trimmed on your device. Jolts near where you began or finished are never sent, so the map can't be used to infer home or work.</li>
        <li>Your IP address is used briefly at the edge to filter abuse. It is never stored alongside a jolt.</li>
        <li>The raw motion sensor stream never leaves your phone. Only the small number of detected jolts are uploaded.</li>
      </ul>

      <h2>What it asks for</h2>
      <ul>
        <li>
          <strong>Location</strong> — required. Every detected event needs a where.
        </li>
        <li>
          <strong>Motion sensors</strong> — required (iOS only prompts; Android and desktop expose them without a permission gate). The actual detection runs on-device; raw streams never leave your phone.
        </li>
      </ul>
      <p>
        The app explicitly asks before any sensor turns on. If you tap deny by accident, the page shows you exactly where to re-enable it in your browser settings.
      </p>

      <h2>Offline</h2>
      <p>
        Drives often go through no-signal stretches. Every detected event is queued locally in your browser's IndexedDB before any upload attempt. When you're back online — or when you reopen the app on Wi-Fi later — the queue drains automatically. Closing the app doesn't lose anything; queued events persist until the upload succeeds.
      </p>
      <p>
        If an upload fails mid-batch (signal drops while we're talking to the server), the batch is re-queued and retried on the next tick or the next time the browser fires the <code>online</code> event.
      </p>

      <h2>iOS in this alpha</h2>
      <p>
        This is the alpha launch (v1). On iOS the app currently runs as a
        Progressive Web App — which means sensors stop the moment the tab
        loses focus. Keep the joltstream tab in front and the screen on while
        you drive, and everything works fine. A native iOS app with proper
        background sensing is on the way in v2.
      </p>
      <p>
        Android isn't affected — the Android app uses Capacitor and a foreground
        service for proper background collection.
      </p>

      <h2>Licence</h2>
      <p>
        Source code: proprietary, all rights reserved. Time Back Solutions Ltd.
      </p>
      <p>
        Aggregated event data will be published under an open data licence
        (ODbL or CC-BY 4.0, decision pending) once the dataset reaches
        useful coverage and a stable shape — the intention is for the
        road-quality dataset itself to be a public good even though the
        code that produces it is closed.
      </p>

      <h2>Privacy</h2>
      <p>
        See the <a href="/privacy">privacy notice</a> for a full breakdown of
        what we collect, why, and your rights under UK GDPR.
      </p>
    </div>
  );
}
